import { Action, AsyncAction } from '../types/types';
import {
	GetBroadcastServer,
	GetEventsSessionRtmpEndpoint,
	GetEventsLegacyRtmpEndpoint,
	StartBroadcast,
	StopBroadcast,
	ManuallyCreateBroadcastServer,
	AutoStartBroadcast,
	SetDatabaseStreamState,
	GetDatabaseStreamState,
	GetRtmpEndpoint,
	UpdateBroadcastEndpoint,
	UpdateUseCustomBroadcastEndpoint,
	ResetBroadcastServer,
} from '../../connection/broadcasting';
import { BroadcastStats } from '../reducers/broadcasting';
import { GetBroadcastStreamingConfiguration } from '../../connection/showrooms';
import { EditRecordingMetadata } from '../../connection/media';
import { CustomDestination } from '../reducers/settings';

export interface CustomRtmpData {
	useCustomEndpoint: boolean;
	endpoint: {
		name: string,
		url: string,
		stream_key: string
	}
}

export const GET_BROADCAST_SERVER = 'GET_BROADCAST_SERVER';
export function getBroadcastServer(token: string, meeting: number): AsyncAction {
	return {
		type: GET_BROADCAST_SERVER,
		promise: GetBroadcastServer(token, meeting),
	};
}

export const GET_BROADCAST_ENDPOINT = 'GET_BROADCAST_ENDPOINT';
export function getBroadcastEndpoint(token: string, meeting: number) {
	return {
		type: GET_BROADCAST_ENDPOINT,
		promise: GetRtmpEndpoint(token, meeting),
	};
}

export const START_BROADCAST = 'START_BROADCAST';
export function startBroadcast(token: string, meeting: number, resolution: string, broadcast?: boolean, record?: boolean): AsyncAction {
	return {
		type: START_BROADCAST,
		promise: StartBroadcast(token, meeting, resolution, broadcast, record),
	};
}

export const STOP_BROADCAST = 'STOP_BROADCAST';
export function stopBroadcast(token: string, meeting: number): AsyncAction {
	return {
		type: STOP_BROADCAST,
		promise: StopBroadcast(token, meeting),
	};
}

export const MANUALLY_CREATE_BROADCAST_SERVER = 'MANUALLY_CREATE_BROADCAST_SERVER';
export function manuallyCreateBroadcastServer(token: string, meeting: number): AsyncAction {
	return {
		type: MANUALLY_CREATE_BROADCAST_SERVER,
		promise: ManuallyCreateBroadcastServer(token, meeting),
	};
}

export const SET_BROADCAST_STATE = 'SET_BROADCAST_STATE';
export function setBroadcastState(state: number): Action {
	return {
		type: SET_BROADCAST_STATE,
		payload: state,
	};
}

export const SET_PREVIOUS_BROADCAST_STATE = 'SET_PREVIOUS_BROADCAST_STATE';
export function setPreviousBroadcastState(state: number): Action {
	return {
		type: SET_PREVIOUS_BROADCAST_STATE,
		payload: state,
	};
}

export const SET_BROADCAST_STATS = 'SET_BROADCAST_STATS';
export function setBroadcastStats(stats: BroadcastStats): Action {
	return {
		type: SET_BROADCAST_STATS,
		payload: stats,
	};
}

export const GET_EVENTS_SESSION_BROADCAST_ENDPOINT = 'GET_EVENTS_SESSION_BROADCAST_ENDPOINT';
export function getEventsSessionRtmpEndpoint(token: string, session_uuid: string, language: string) {
	return {
		type: GET_EVENTS_SESSION_BROADCAST_ENDPOINT,
		promise: GetEventsSessionRtmpEndpoint(token, session_uuid, language),
	};
}

export const GET_EVENTS_LEGACY_BROADCAST_ENDPOINT = 'GET_EVENTS_LEGACY_BROADCAST_ENDPOINT';
export function getEventsLegacyBroadcastEndpoint(token: string, channel: number) {
	return {
		type: GET_EVENTS_LEGACY_BROADCAST_ENDPOINT,
		promise: GetEventsLegacyRtmpEndpoint(token, channel),
	};
}

export const GET_SHOWROOMS_BROADCAST_ENDPOINT = 'GET_SHOWROOMS_BROADCAST_ENDPOINT';
export function getShowroomsBroadcastEndpoint(token: string, sessionID: string) {
	return {
		type: GET_SHOWROOMS_BROADCAST_ENDPOINT,
		promise: GetBroadcastStreamingConfiguration(token, sessionID),
	};
}

export const UPDATE_BROADCAST_ENDPOINT = 'UPDATE_RTMP_ENDPOINT';
export function updateCustomBroadcastEndpoint(token: string, meeting: number, name: string, rtmpAddress: string, streamKey: string) {
	return {
		type: UPDATE_BROADCAST_ENDPOINT,
		promise: UpdateBroadcastEndpoint(token, meeting, name, rtmpAddress, streamKey),
	};
}

export const SET_CUSTOM_BROADCAST_ENDPOINT = 'SET_CUSTOM_BROADCAST_ENDPOINT';
export function setCustomBroadcastEndpoint(data: CustomRtmpData): Action {
	return {
		type: SET_CUSTOM_BROADCAST_ENDPOINT,
		payload: data,
	};
}

export const SET_USE_CUSTOM_BROADCAST_ENDPOINT = 'SET_USE_CUSTOM_BROADCAST_ENDPOINT';
export function updateUseCustomBroadcastEndpoint(token: string, meeting: number, useCustomEndpoint: boolean) {
	return {
		type: SET_USE_CUSTOM_BROADCAST_ENDPOINT,
		promise: UpdateUseCustomBroadcastEndpoint(token, meeting, useCustomEndpoint),
	};
}

export const SET_STREAM_STATE = 'SET_STREAM_STATE';
export function setStreamState(status: number) {
	return {
		type: SET_STREAM_STATE,
		payload: status,
	};
}

export const SET_DB_STREAM_STATE = 'SET_DB_STREAM_STATE';
export function setDatabaseStreamState(token: string, meeting: number, status: number): AsyncAction {
	return {
		type: SET_DB_STREAM_STATE,
		promise: SetDatabaseStreamState(token, meeting, status),
	};
}

export const GET_DB_STREAM_STATE = 'GET_DB_STREAM_STATE';
export function getDatabaseStreamState(token: string, meeting: number): AsyncAction {
	return {
		type: GET_DB_STREAM_STATE,
		promise: GetDatabaseStreamState(token, meeting),
	};
}

export const AUTO_START_BROADCAST = 'AUTO_START_BROADCAST';
export function autoStartBroadcast(token: string, meeting: number): AsyncAction {
	return {
		type: AUTO_START_BROADCAST,
		promise: AutoStartBroadcast(token, meeting),
	};
}

export const SET_GO_LIVE_BUTTON_CLICKED = 'SET_GO_LIVE_BUTTON_CLICKED';
export function setGoLiveButtonClicked(goLiveButtonClicked: boolean): Action {
	return {
		type: SET_GO_LIVE_BUTTON_CLICKED,
		payload: goLiveButtonClicked,
	};
}

export const RESET_BROADCAST_SERVER = 'RESET_BROADCAST_SERVER';
export function resetBroadcastServer(token: string, meeting: number): AsyncAction {
	return {
		type: RESET_BROADCAST_SERVER,
		promise: ResetBroadcastServer(token, meeting),
	};
}

export const SET_RECORDING_OPTION = 'SET_RECORDING_OPTION';
export function setRecordingOption(selectedRecordingOption: number): Action {
	return {
		type: SET_RECORDING_OPTION,
		payload: selectedRecordingOption,
	};
}

export const SYNC_BROADCAST_REDUCER_STATE = 'SYNC_BROADCAST_REDUCER_STATE';
export function syncBroadcastReducerState(state: any): Action {
	return {
		type: SYNC_BROADCAST_REDUCER_STATE,
		payload: state,
	};
}

export const RESET_BROADCAST_REDUCER_STATE = 'RESET_BROADCAST_REDUCER_STATE';
export function resetBroadcastReducerState(): Action {
	return {
		type: RESET_BROADCAST_REDUCER_STATE,
		payload: undefined,
	};
}

export const SET_BROADCAST_STARTING_SHOT = 'SET_BROADCAST_STARTING_SHOT';
export function setBroadcastStartingShot(shotId: number): Action {
	return {
		type: SET_BROADCAST_STARTING_SHOT,
		payload: shotId,
	};
}

export const SET_BROADCAST_STARTED_BY = 'SET_BROADCAST_STARTED_BY';
export function setBroadcastStartedBy(userId: number): Action {
	return {
		type: SET_BROADCAST_STARTED_BY,
		payload: userId,
	};
}

export const SET_CURRENT_RECORDING_STREAM_ID = 'SET_CURRENT_RECORDING_STREAM_ID';
export function setCurrentRecordingStreamId(streamId: string): Action {
	return {
		type: SET_CURRENT_RECORDING_STREAM_ID,
		payload: streamId,
	};
}

export const EDIT_RECORDING_METADATA = 'EDIT_RECORDING_METADATA';
export function editRecordingMetadata(
	token: string,
	streamId: string,
	broadcastStartedBy: number,
	broadcastStartingShotId: number,
): AsyncAction {
	return {
		type: EDIT_RECORDING_METADATA,
		promise: EditRecordingMetadata(token, streamId, broadcastStartedBy, broadcastStartingShotId),
	};
}

export const SET_GO_LIVE_FROM_MODAL = 'GO_LIVE_FROM_MODAL';
export function setGoLiveFromModal(shouldGoLive: boolean): Action {
	return {
		type: SET_GO_LIVE_FROM_MODAL,
		payload: shouldGoLive,
	};
}

export const SET_ADDING_DESTINATION = 'SET_ADDING_DESTINATION';
export function setAddingDestination(addingDestination: CustomDestination | null): Action {
	return {
		type: SET_ADDING_DESTINATION,
		payload: addingDestination,
	};
}

export const SET_EDITING_DESTINATION = 'SET_EDITING_DESTINATION';
export function setEditingDestination(editingDestination: string): Action {
	return {
		type: SET_EDITING_DESTINATION,
		payload: editingDestination,
	};
}
